import { UseFormReturn } from 'react-hook-form';
import { PowerProfileDataPoint, PowerProfileMode, powerProfileModes } from 'greener-ui-library';
import { PowerProfileValidationResponse } from 'hooks/useValidatePowerProfileFile';
import { LoadProfile } from 'types';
import { BlueprintFormData } from '../types';
import {
  convertCsvToPowerProfileData,
  convertPowerProfileDataToCsv,
  generateManualModeChartCsv,
} from './powerProfileCsvConverter';

/**
 * Map validation error messages to static translation paths
 * Maps backend validation error keys to translation keys for consistent error messaging
 */
export const validationErrorMap: Record<string, string> = {
  'validations.required': 'validations.required',
  'validations.time.invalidFormat': 'validations.time.invalidFormat',
  'validations.time.invalidHours': 'validations.time.invalidHours',
  'validations.time.invalidMinutes': 'validations.time.invalidMinutes',
  'validations.time.endBeforeStart': 'validations.time.endBeforeStart',
  'validations.number': 'validations.number',
  'validations.integer': 'validations.integer',
  'validations.fieldMustBePositive': 'validations.fieldMustBePositive',
  'validations.notType': 'validations.notType',
};

/**
 * Interface representing the result of file processing for power profile data
 */
export interface FileProcessingResult {
  originalData: PowerProfileDataPoint[];
  processedFileStatistics?: Partial<LoadProfile>;
  warnings: string[];
  errors: string[];
  success: boolean;
}

/**
 * Helper function to get validation error translation
 * Maps backend error messages to translation keys for consistent error messaging
 * @param errorMessage - The error message from backend validation
 * @returns The translation key for the error message, or the original message if no mapping exists
 */
export const getValidationError = (errorMessage?: string) => {
  if (!errorMessage) {
    return '';
  }
  return validationErrorMap[errorMessage] || errorMessage;
};

/**
 * Function to generate and update CSV data in form
 * Updates both raw and edited CSV data based on current form state and chart data
 * @param formMethods - React Hook Form methods for the blueprint form
 * @param powerProfileMode - Current power profile mode (manual or file)
 * @param fileProcessingResult - Result of file processing (null for manual mode)
 * @param originalFileName - Original filename for file mode (null for manual mode)
 * @param powerProfileDataFromApi - Power profile data fetched from API in edit mode (optional)
 */
export const updatePowerProfileCsvData = (
  formMethods: UseFormReturn<BlueprintFormData>,
  powerProfileMode: PowerProfileMode,
  fileProcessingResult: FileProcessingResult | null,
  originalFileName: string | null,
  powerProfileDataFromApi?: PowerProfileDataPoint[] | null
) => {
  if (powerProfileMode === powerProfileModes.manual) {
    // Manual mode: generate CSV from inputs
    const peakPower = formMethods.watch('peakPowerKw');
    const averagePower = formMethods.watch('averagePowerKw');
    const workingDaysData = formMethods.watch('workingDays');
    const workingHoursStart = formMethods.watch('workingHoursStart');
    const workingHoursEnd = formMethods.watch('workingHoursEnd');
    const peaksPerDay = formMethods.watch('peaksPerDay');
    const peaksDuration = formMethods.watch('peaksDuration');
    const restDayAveragePowerKw = formMethods.watch('restDayAveragePowerKw');

    // Check if values are defined (not undefined/null), allow 0
    if (
      peakPower !== undefined &&
      peakPower !== null &&
      averagePower !== undefined &&
      averagePower !== null &&
      workingDaysData &&
      workingHoursStart &&
      workingHoursEnd
    ) {
      const rawCsv = generateManualModeChartCsv(
        peakPower,
        averagePower,
        workingDaysData,
        workingHoursStart,
        workingHoursEnd,
        peaksPerDay,
        peaksDuration,
        restDayAveragePowerKw
      );

      // Set raw CSV (from inputs)
      formMethods.setValue('rawPowerProfileCsv', rawCsv, { shouldValidate: false });
      formMethods.setValue('editedPowerProfileCsv', rawCsv, { shouldValidate: false });
    }
  } else if (powerProfileMode === powerProfileModes.file) {
    // File mode: use processed file data or data from API (edit mode)
    // Priority:
    // 1. fileProcessingResult (freshly uploaded file)
    // 2. CSV in form (preserves data after navigation or after mode switch if saved)
    // 3. powerProfileDataFromApi (first load in edit mode or after mode switch if not saved)
    const rawCsvFromForm = formMethods.getValues('rawPowerProfileCsv');
    const hasCsvInForm = rawCsvFromForm && rawCsvFromForm !== '';

    let sourceData = fileProcessingResult?.originalData;
    let rawCsv: string | null = null;

    if (sourceData) {
      // We have fresh file processing result - use it
      rawCsv = convertPowerProfileDataToCsv(sourceData);
    } else if (hasCsvInForm) {
      // We have CSV in form - use it (preserves data after navigation or if saved after mode switch)
      rawCsv = rawCsvFromForm;
      sourceData = convertCsvToPowerProfileData(rawCsv);
    } else if (powerProfileDataFromApi) {
      // No CSV in form - restore from API (first load or after mode switch if not saved)
      // This ensures data is shown on first edit entry, and restored if user switched mode but didn't save
      sourceData = powerProfileDataFromApi;
      rawCsv = convertPowerProfileDataToCsv(sourceData);
    }

    if (sourceData && rawCsv) {
      formMethods.setValue('rawPowerProfileCsv', rawCsv, { shouldValidate: true });
      formMethods.setValue('editedPowerProfileCsv', rawCsv, { shouldValidate: false });

      // Set original file name for file mode
      if (originalFileName) {
        formMethods.setValue('originalFileName', originalFileName, { shouldValidate: false });
      }
    }
  }
};

/**
 * Function to check if chart has data
 * Validates that power profile has any data (either from manual inputs, uploaded file, or API)
 * @param formMethods - React Hook Form methods for the blueprint form
 * @param currentMode - Current power profile mode
 * @param fileProcessingResult - Result of file processing (for file mode)
 * @param editedChartData - Manually edited chart data
 * @param powerProfileDataFromApi - Power profile data from API (for edit mode)
 * @returns True if user has started entering data (even partially), false otherwise
 *
 * Note: In manual mode, returns true if ANY field is filled (peakPower, averagePower, or workingDays)
 * because the chart can render with partial data. This ensures the mode switch confirmation modal
 * appears even when user has only filled some fields.
 */
export const hasChartData = (
  formMethods: UseFormReturn<BlueprintFormData>,
  currentMode: PowerProfileMode,
  fileProcessingResult: FileProcessingResult | null,
  powerProfileDataFromApi: PowerProfileDataPoint[] | null
) => {
  if (currentMode === powerProfileModes.manual) {
    // Manual mode: check if ANY field has been filled (chart can render with partial data)
    const peakPower = formMethods.watch('peakPowerKw');
    const averagePower = formMethods.watch('averagePowerKw');
    const workingDaysData = formMethods.watch('workingDays');

    // Check if user has started filling any fields
    // Chart renders even with just peakPower and averagePower, so we need to show modal
    const hasAnyData = !!(peakPower || averagePower || (workingDaysData && workingDaysData.length > 0));

    // Working hours have default values ('08:00', '15:00'), so we don't check them
    // as they don't indicate user input

    return hasAnyData;
  } else if (currentMode === powerProfileModes.file) {
    // File mode: check if file was uploaded or data exists from API
    // Also check if CSV data exists in form (in case local state was lost after navigation)
    const rawCsv = formMethods.getValues('rawPowerProfileCsv');
    const editedCsv = formMethods.getValues('editedPowerProfileCsv');

    return !!(fileProcessingResult?.success || powerProfileDataFromApi?.length || rawCsv || editedCsv);
  }

  return false;
};

/**
 * Function to handle mode change
 * Manages switching between manual and file power profile modes with confirmation dialog
 * @param newMode - The new power profile mode to switch to
 * @param currentMode - The current power profile mode
 * @param formMethods - React Hook Form methods for the blueprint form
 * @param setPendingMode - State setter for pending mode change
 * @param setShowModeSwitchConfirmation - State setter for showing confirmation dialog
 * @param fileProcessingResult - Result of file processing (for file mode)
 * @param powerProfileDataFromApi - Power profile data from API (for edit mode)
 */
export const handleModeChange = (
  newMode: PowerProfileMode,
  currentMode: PowerProfileMode,
  formMethods: UseFormReturn<BlueprintFormData>,
  setPendingMode: (mode: PowerProfileMode | null) => void,
  setShowModeSwitchConfirmation: (show: boolean) => void,
  fileProcessingResult: FileProcessingResult | null,
  powerProfileDataFromApi: PowerProfileDataPoint[] | null
) => {
  // If chart has data and user is switching modes, show confirmation
  if (
    hasChartData(formMethods, currentMode, fileProcessingResult, powerProfileDataFromApi) &&
    newMode !== currentMode
  ) {
    setPendingMode(newMode);
    setShowModeSwitchConfirmation(true);
  } else {
    // No data or same mode, switch immediately
    formMethods.setValue('powerProfileMode', newMode, { shouldValidate: false });
  }
};

/**
 * Function to handle mode switch confirmation
 * Confirms mode change and resets all form data and state to default values
 * @param pendingMode - The pending mode to switch to (null if no pending change)
 * @param formMethods - React Hook Form methods for the blueprint form
 * @param setFileProcessingResult - State setter for file processing result
 * @param setOriginalFileName - State setter for original filename
 * @param setShowModeSwitchConfirmation - State setter for showing confirmation dialog
 * @param setPendingMode - State setter for pending mode change
 */
export const handleModeSwitchConfirm = (
  pendingMode: PowerProfileMode | null,
  formMethods: UseFormReturn<BlueprintFormData>,

  setFileProcessingResult: (result: FileProcessingResult | null) => void,
  setOriginalFileName: (name: string | null) => void,
  setShowModeSwitchConfirmation: (show: boolean) => void,
  setPendingMode: (mode: PowerProfileMode | null) => void
) => {
  if (pendingMode) {
    formMethods.setValue('powerProfileMode', pendingMode, { shouldValidate: false });
    // Reset form data to default values when switching modes without triggering validation
    formMethods.setValue('peakPowerKw', undefined, { shouldValidate: false });
    formMethods.setValue('averagePowerKw', undefined, { shouldValidate: false });
    formMethods.setValue('peaksPerDay', undefined, { shouldValidate: false });
    formMethods.setValue('peaksDuration', undefined, { shouldValidate: false });
    formMethods.setValue('restDayAveragePowerKw', undefined, { shouldValidate: false });
    formMethods.setValue('workingDays', [], { shouldValidate: false });
    formMethods.setValue('workingHoursStart', '08:00', { shouldValidate: false });
    formMethods.setValue('workingHoursEnd', '15:00', { shouldValidate: false });

    // Reset CSV data fields
    formMethods.setValue('rawPowerProfileCsv', '', { shouldValidate: false });
    formMethods.setValue('editedPowerProfileCsv', '', { shouldValidate: false });
    formMethods.setValue('originalFileName', '', { shouldValidate: false });

    // Reset chart data and file processing results
    setFileProcessingResult(null);
    setOriginalFileName(null);
  }
  setShowModeSwitchConfirmation(false);
  setPendingMode(null);
};

/**
 * Function to handle file upload
 * Processes uploaded CSV file for power profile data validation and chart generation
 * @param file - The CSV file to upload and process
 * @param validateFile - Function to validate the uploaded file via backend API
 * @param setFileProcessingResult - State setter for file processing result
 * @param setOriginalFileName - State setter for original filename
 * @param setIsProcessingFile - State setter for file processing status
 * @param updatePowerProfileCsvDataCallback - Callback to update CSV data after successful processing
 */
export const handleFileUpload = async (
  file: File,
  validateFile: (file: File) => Promise<PowerProfileValidationResponse>,
  setFileProcessingResult: (result: FileProcessingResult | null) => void,
  setOriginalFileName: (name: string | null) => void,
  setIsProcessingFile: (processing: boolean) => void,
  updatePowerProfileCsvDataCallback: () => void
) => {
  setIsProcessingFile(true);
  setFileProcessingResult(null);
  setOriginalFileName(file.name);

  try {
    // Send file to backend for validation and processing
    const validationResponse = await validateFile(file);

    if (!validationResponse.success) {
      setFileProcessingResult({
        originalData: [],
        warnings: validationResponse.warnings || [],
        errors: validationResponse.errors || ['Validation failed'],
        success: false,
      });
      return;
    }

    const allData = validationResponse.data || [];

    // Set the processed data as the initial chart data
    setFileProcessingResult({
      originalData: allData,
      processedFileStatistics: validationResponse.processedFileStatistics,
      warnings: validationResponse.warnings || [],
      errors: [],
      success: true,
    });

    // Update CSV data after successful file processing
    updatePowerProfileCsvDataCallback();

    // Scroll to chart after successful file processing
    setTimeout(() => {
      const chartElement =
        document.querySelector('[data-testid="power-profile-chart"]') || document.querySelector('.recharts-wrapper');
      if (chartElement) {
        chartElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  } catch (error) {
    setFileProcessingResult({
      originalData: [],
      warnings: [],
      errors: [`File validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`],
      success: false,
    });
  } finally {
    setIsProcessingFile(false);
  }
};

/**
 * Aggregate power profile data by minute, returning the maximum power value for each minute
 * @param data - Array of PowerProfileDataPoint with second-level granularity
 * @returns Array of PowerProfileDataPoint aggregated by minute with maximum power values
 */
const aggregationCache = new Map<string, PowerProfileDataPoint[]>();
export const aggregateDataByMinute = (data: PowerProfileDataPoint[]): PowerProfileDataPoint[] => {
  if (!data || data.length === 0) {
    return [];
  }

  const cacheKey = JSON.stringify(data);
  const cached = aggregationCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const minuteMap = new Map<string, { maxPower: number; point: PowerProfileDataPoint }>();

  // Group data by minute and track maximum power
  for (const point of data) {
    // Extract minute from timestamp (dayIndex:HH:MM)
    const parts = point.timestamp.split(':');
    if (parts.length < 3) {
      continue;
    }

    const dayIndex = parts[0];
    const hours = parts[1];
    const minutes = parts[2];
    const minuteKey = `${dayIndex}:${hours}:${minutes}`;

    const existing = minuteMap.get(minuteKey);
    if (!existing || point.power > existing.maxPower) {
      minuteMap.set(minuteKey, {
        maxPower: point.power,
        point: { ...point, timestamp: minuteKey },
      });
    }
  }

  // Convert map to array and sort by timestamp
  const result = Array.from(minuteMap.values())
    .map(({ point }) => point)
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp, undefined, { numeric: true }));

  aggregationCache.set(cacheKey, result);

  return result;
};
